import React, { useCallback, useContext, useMemo, useState } from 'react';

import PropType from 'prop-types';
import { useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { GuidService } from 'ui-components/services/guid-service';

import { CurrencyPropType } from '../../../../prop-types/currency';
import { ChildrenPropType } from '../../../../prop-types/children';
import Global from '../../../../store/global-selectors';
import messages from './messages';
import useSaveFulfillmentPlanItems from './use-save-fulfillment-plan-items';
import { useIsManualOrder } from '../../../../hooks/use-is-manual-order';
import { useEnableReship } from '../../../../feature-flags/hooks/use-enable-reship';
import { useEnableAddShipmentItems } from '../../../../feature-flags/hooks/use-enable-add-shipment-items';

const initialState = {
  addLineItem: noop,
  deleteItem: noop,
  displayItems: emptyArray,
  errors: emptyArray,
  items: emptyArray,
  save: noop,
  isSaving: false,
  shouldPreventItemEdits: false,
  updateItemProperties: noop,
};

const EditItemsContext = React.createContext(initialState);

export const useEditItemsContext = () => useContext(EditItemsContext);

export const DisabledCodes = {
  SPLIT: 'split',
  MARKETPLACE_ORDER: 'marketplace_order',
};

const getReadonlyState = (item) => {
  if (item.isLinkedToSplitSalesOrderItem) {
    return {
      code: DisabledCodes.SPLIT,
      reasonMessageDescriptor: messages.splitShipDisabledMessage,
    };
  }

  if (item.isLinkedToMarketplaceOrder) {
    return {
      code: DisabledCodes.MARKETPLACE_ORDER,
      reasonMessageDescriptor: messages.marketplaceOrderDisabledMessage,
    };
  }

  return {};
};

const buildAddedItem = ({ fulfillmentPlanId, salesOrderId, sellerCurrencyCode }) => {
  return {
    isAdded: true,
    id: GuidService.newGuid(),
    fulfillmentPlanId,
    salesOrderId,
    name: '',
    sku: '',
    quantity: 1,
    unitPrice: {
      value: 0.0,
      code: sellerCurrencyCode,
    },
  };
};

const normalizeItem = (entityItem) => {
  return {
    ...entityItem,
    id: entityItem.fulfillmentPlanItemId || GuidService.newGuid(),
    readonly: getReadonlyState(entityItem),
  };
};

const getInitialItems = ({ initialItems, createEmptyItem, shouldPreventItemEdits }) => {
  if (!shouldPreventItemEdits && !initialItems.length) {
    const emptyItem = createEmptyItem();
    return [emptyItem];
  }
  return initialItems.map(normalizeItem);
};

const getShouldPreventItemEdits = (isReshipEnabled, isManualOrder, isAddShipmentItemsEnabled) => {
  if (isReshipEnabled) {
    // if flag is enabled, don't disable item edits at all
    return false;
  }
  // if flag is off use current behavior
  if (isManualOrder) {
    // Don't disable for manual orders
    return false;
  }
  if (isAddShipmentItemsEnabled) {
    // Don't disable for add shipment items
    return false;
  }

  // disable for non-manual orders
  return true;
};

const EditItemsContextProvider = ({ children, items: initialItems, fulfillmentPlanId, onSuccess = noop, salesOrderId }) => {
  const isReshipEnabled = useEnableReship();
  const isManualOrder = useIsManualOrder(salesOrderId);
  const isAddShipmentItemsEnabled = useEnableAddShipmentItems();
  const shouldPreventItemEdits = getShouldPreventItemEdits(isReshipEnabled, isManualOrder, isAddShipmentItemsEnabled);

  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);

  const createEmptyItem = useCallback(() => {
    const salesOrderIdForNewItems = isManualOrder ? salesOrderId : null;

    return normalizeItem(
      buildAddedItem({
        fulfillmentPlanId,
        salesOrderId: salesOrderIdForNewItems,
        sellerCurrencyCode,
      }),
    );
  }, [fulfillmentPlanId, isManualOrder, salesOrderId, sellerCurrencyCode]);

  const [items, setItems] = useState(() =>
    getInitialItems({
      initialItems,
      createEmptyItem,
      shouldPreventItemEdits,
    }),
  );
  const [errors, setErrors] = useAsyncSafeState(emptyArray);

  const onError = useCallback(
    (updatedErrors) => {
      setErrors(updatedErrors);
    },
    [setErrors],
  );

  const { isSaving, save } = useSaveFulfillmentPlanItems(fulfillmentPlanId, onSuccess, onError);

  const addLineItem = useCallback(() => {
    const newItem = createEmptyItem();
    setItems((prevItems) => [...prevItems, newItem]);
  }, [createEmptyItem]);

  const deleteItem = useCallback(
    (fulfillmentPlanItemId) => {
      setItems((prevItems) => {
        return prevItems
          .filter((prevItem) => {
            return prevItem.id === fulfillmentPlanItemId ? !prevItem.isAdded : true;
          })
          .map((prevItem) => {
            if (prevItem.id === fulfillmentPlanItemId) {
              return {
                ...prevItem,
                isDeleted: true,
              };
            }

            return prevItem;
          });
      });
    },
    [setItems],
  );

  const updateItemProperties = useCallback(
    (id, updatedItemProperties) => {
      setItems((prevItems) =>
        prevItems.map((prevItem) => {
          if (prevItem.id === id) {
            return {
              ...prevItem,
              ...updatedItemProperties,
            };
          }
          return prevItem;
        }),
      );
    },
    [setItems],
  );

  const displayItems = useMemo(() => {
    return items.filter((item) => !item.isDeleted);
  }, [items]);

  const value = useMemo(
    () => ({
      addLineItem,
      deleteItem,
      displayItems,
      errors,
      items,
      save,
      isSaving,
      shouldPreventItemEdits,
      updateItemProperties,
    }),
    [addLineItem, deleteItem, displayItems, errors, items, save, isSaving, shouldPreventItemEdits, updateItemProperties],
  );

  return <EditItemsContext.Provider value={value}>{children}</EditItemsContext.Provider>;
};

EditItemsContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
  items: PropType.arrayOf(
    PropType.shape({
      fulfillmentPlanItemId: PropType.string.isRequired,
      unitPrice: CurrencyPropType,
    }),
  ).isRequired,
  fulfillmentPlanId: PropType.string.isRequired,
  onSuccess: PropType.func,
  salesOrderId: PropType.string.isRequired,
};

export default EditItemsContextProvider;
